import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type {
  SageVipConnectionDto,
  SageVipUpsertRequestDto,
} from "../dtos/SageVipConnectDto"
import { sageVipConnectorService } from "../infrastructure/sageVipConnectorService"

interface SageVipConnectorState {
  connection: SageVipConnectionDto | null
  isLoading: boolean
  isSubmitting: boolean
  isDeleting: boolean
  error: string | null
  isSuccess: boolean
}

const initialState: SageVipConnectorState = {
  connection: null,
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  error: null,
  isSuccess: false,
}

export const fetchSageVipConnection = createAsyncThunk(
  "sageVipConnector/fetch",
  async (organizationId: string | null, { rejectWithValue }) => {
    try {
      const connection =
        await sageVipConnectorService.getVipConnection(organizationId)
      return connection
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { message?: string }).message ?? "Failed to fetch connection",
      )
    }
  },
)

export const upsertSageVipConnection = createAsyncThunk(
  "sageVipConnector/upsert",
  async (
    {
      organizationId,
      dto,
    }: { organizationId: string | null; dto: SageVipUpsertRequestDto },
    { rejectWithValue },
  ) => {
    try {
      await sageVipConnectorService.upsertVipConnection(organizationId, dto)
      const connection =
        await sageVipConnectorService.getVipConnection(organizationId)
      return connection
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { message?: string }).message ?? "Failed to save connection",
      )
    }
  },
)

export const deleteSageVipConnection = createAsyncThunk(
  "sageVipConnector/delete",
  async (organizationId: string | null, { rejectWithValue }) => {
    try {
      await sageVipConnectorService.deleteVipConnection(organizationId)
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { message?: string }).message ??
          "Failed to delete connection",
      )
    }
  },
)

const sageVipConnectorSlice = createSlice({
  name: "sageVipConnector",
  initialState,
  reducers: {
    clearSageVipState: (state) => {
      state.connection = null
      state.error = null
      state.isSuccess = false
    },
    resetSageVipSuccess: (state) => {
      state.isSuccess = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSageVipConnection.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSageVipConnection.fulfilled, (state, action) => {
        state.isLoading = false
        state.connection = action.payload
      })
      .addCase(fetchSageVipConnection.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(upsertSageVipConnection.pending, (state) => {
        state.isSubmitting = true
        state.error = null
        state.isSuccess = false
      })
      .addCase(upsertSageVipConnection.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.connection = action.payload
        state.isSuccess = true
      })
      .addCase(upsertSageVipConnection.rejected, (state, action) => {
        state.isSubmitting = false
        state.error = action.payload as string
      })
      .addCase(deleteSageVipConnection.pending, (state) => {
        state.isDeleting = true
        state.error = null
      })
      .addCase(deleteSageVipConnection.fulfilled, (state) => {
        state.isDeleting = false
        state.connection = null
      })
      .addCase(deleteSageVipConnection.rejected, (state, action) => {
        state.isDeleting = false
        state.error = action.payload as string
      })
  },
})

export const { clearSageVipState, resetSageVipSuccess } =
  sageVipConnectorSlice.actions

export const selectSageVipConnection = (state: RootState) =>
  state.sageVipConnector.connection
export const selectSageVipIsLoading = (state: RootState) =>
  state.sageVipConnector.isLoading
export const selectSageVipIsSubmitting = (state: RootState) =>
  state.sageVipConnector.isSubmitting
export const selectSageVipIsDeleting = (state: RootState) =>
  state.sageVipConnector.isDeleting
export const selectSageVipError = (state: RootState) =>
  state.sageVipConnector.error
export const selectSageVipIsSuccess = (state: RootState) =>
  state.sageVipConnector.isSuccess

export default sageVipConnectorSlice.reducer
